import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

// The three.js viewer, ported from prototype/viewer/viewer.js. Self-contained: give it a container element and
// cp-api's asset files (GET /v1/creations/{id}/asset), then show versions (GET .../versions/{n}). Materials are
// named by texture set id. Every file comes from a short-lived signed URL, so nothing is fetched again once
// loaded; a version whose URLs expired is fetched again by the caller (see `showVersion`).

const VIEWS = { front: 0, right: 90, back: 180, left: -90 };

export class Viewer {
  constructor(container) {
    this.container = container;
    this.materials = {};  // texture set id -> material
    this.originals = {};  // texture set id -> { map, orm } from the GLB, for sets a version leaves unchanged
    this.meshes = {};     // texture set id -> meshes drawn with it (their UVs, for the UV export)
    this.shownVersion = 0;
    this.loader = new THREE.TextureLoader().setCrossOrigin('anonymous');

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    container.appendChild(renderer.domElement);
    this.renderer = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x9a9ea4);
    // Metallic surfaces need something to reflect; a neutral room gives PBR its look.
    scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;
    const sun = new THREE.DirectionalLight(0xffffff, 1.5);
    sun.position.set(2, 4, 3);
    scene.add(sun);
    this.scene = scene;

    this.camera = new THREE.PerspectiveCamera(35, 1, 0.05, 50);
    this.controls = new OrbitControls(this.camera, renderer.domElement);
    this.controls.enableDamping = true;

    this.resize = this.resize.bind(this);
    window.addEventListener('resize', this.resize);
    this.resize();
    renderer.setAnimationLoop(() => { this.controls.update(); renderer.render(scene, this.camera); });
  }

  resize() {
    const { clientWidth: w, clientHeight: h } = this.container;
    if (!w || !h) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  dispose() {
    window.removeEventListener('resize', this.resize);
    this.renderer.setAnimationLoop(null);
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  /**
   * Loads the GLB; returns { ms, triangles, sets }. `onProgress({ loaded, total })` follows the download (bytes;
   * `total` is 0 if the server sent no Content-Length).
   */
  async load(assetFiles, { onProgress } = {}) {
    const started = performance.now();
    const loader = new GLTFLoader().setCrossOrigin('anonymous');
    const gltf = await loader.loadAsync(assetFiles.glb_url,
      onProgress && ((e) => onProgress({ loaded: e.loaded, total: e.lengthComputable ? e.total : 0 })));
    const setIds = new Set(Object.keys(assetFiles.textures));
    let triangles = 0;
    gltf.scene.traverse((o) => {
      if (!o.isMesh) return;
      triangles += o.geometry.index ? o.geometry.index.count / 3 : o.geometry.attributes.position.count / 3;
      if (setIds.has(o.material.name)) {
        this.materials[o.material.name] = o.material;
        this.originals[o.material.name] = { map: o.material.map, orm: o.material.roughnessMap };
        (this.meshes[o.material.name] ??= []).push(o);
      }
    });
    this.scene.add(gltf.scene);
    this.frame(gltf.scene);
    return {
      ms: Math.round(performance.now() - started),
      triangles: Math.round(triangles),
      sets: Object.keys(this.materials).sort(),
    };
  }

  // Frame the model: target its centre, back off to fit its largest dimension (a character's height, a vehicle's
  // length), and further when the view is narrow (a phone in portrait), so every camera preset shows all of it.
  frame(object) {
    const box = new THREE.Box3().setFromObject(object);
    const centre = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const extent = Math.max(size.x, size.y, size.z);
    // Across the view, the presets show the model's width (front, back) or its length (sides).
    const across = Math.max(size.x, size.z);
    const halfH = THREE.MathUtils.degToRad(this.camera.fov) / 2;
    const halfW = Math.atan(Math.tan(halfH) * this.camera.aspect);
    const fitWidth = (across / 2 / Math.tan(halfW)) * 1.15 + across / 2;
    const distance = Math.max(extent * 2.4, fitWidth);
    this.controls.target.copy(centre);
    this.camera.position.set(centre.x, centre.y + extent * 0.08, centre.z + distance);
    this.controls.update();
  }

  // Camera presets around the current target. glTF models face +Z.
  view(name) {
    const a = THREE.MathUtils.degToRad(VIEWS[name]);
    const { camera, controls } = this;
    const d = camera.position.distanceTo(controls.target);
    camera.position.set(controls.target.x + d * Math.sin(a), controls.target.y + d * 0.06,
      controls.target.z + d * Math.cos(a));
    controls.update();
  }

  static get views() {
    return Object.keys(VIEWS);
  }

  // Load one map, e.g. ('upper', 'map', url). slot: 'map' (base colour) or 'orm' (glTF metallic-roughness, which
  // three.js reads for both roughnessMap and metalnessMap). Resolves with the texture; does not apply it.
  fetchTexture(set, slot, url) {
    const mat = this.materials[set];
    if (!mat) return Promise.reject(new Error(`unknown texture set ${set}`));
    return new Promise((resolve, reject) => {
      this.loader.load(url, (tex) => {
        const old = slot === 'orm' ? mat.roughnessMap : mat.map;
        tex.flipY = false; // glTF texture convention
        tex.colorSpace = slot === 'map' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        if (old) tex.channel = old.channel;
        resolve(tex);
      }, undefined, () => reject(new Error(`could not load ${set} ${slot}`)));
    });
  }

  assign(set, slot, tex) {
    const mat = this.materials[set];
    const slots = slot === 'orm' ? ['roughnessMap', 'metalnessMap'] : ['map'];
    const old = mat[slots[0]];
    for (const s of slots) mat[s] = tex;
    mat.needsUpdate = true;
    const isOriginal = Object.values(this.originals).some((o) => o.map === old || o.orm === old);
    if (old && old !== tex && !isOriginal) old.dispose();
  }

  /** Back to the asset's own textures (version 0). */
  showOriginal() {
    for (const [set, o] of Object.entries(this.originals)) {
      this.assign(set, 'map', o.map);
      this.assign(set, 'orm', o.orm);
    }
    this.shownVersion = 0;
  }

  /**
   * Show a recipe version (cp-api RecipeVersion). Sets the version did not change (`"base": true` in its outputs)
   * use the GLB's own textures; changed sets are downloaded first and swapped in together, so the model never
   * shows half a version. Throws if a download fails (e.g. the URLs expired): fetch the version again and retry.
   */
  async showVersion(version) {
    if (version.version === 0) return this.showOriginal();
    const sets = version.outputs?.sets ?? {};
    const jobs = [];
    for (const set of Object.keys(this.materials)) {
      for (const [name, slot] of [['basecolor', 'map'], ['orm', 'orm']]) {
        const out = sets[set]?.[name];
        const url = version.urls?.[set]?.[name];
        if (!out || out.base || !url) jobs.push(Promise.resolve([set, slot, null]));
        else jobs.push(this.fetchTexture(set, slot, url).then((tex) => [set, slot, tex]));
      }
    }
    const loaded = await Promise.all(jobs);
    for (const [set, slot, tex] of loaded) {
      this.assign(set, slot, tex ?? (slot === 'map' ? this.originals[set].map : this.originals[set].orm));
    }
    this.shownVersion = version.version;
  }

  // UV export (ported from the prototype): the base colour of every texture set as shown (edited or original), side
  // by side in one image, optionally with the mesh's UV edges drawn over it. glTF textures are not flipped, so UV
  // (u, v) lands on pixel (u * width, v * height). UVs outside 0..1 (tiling) are not wrapped. The textures must
  // have been served with CORS (cross-origin images otherwise taint the canvas).
  drawUvEdges(ctx, set, x0, w, h) {
    const path = new Path2D();
    const channel = this.materials[set].map?.channel ?? 0;
    for (const mesh of this.meshes[set]) {
      const uv = mesh.geometry.attributes[channel ? `uv${channel}` : 'uv'];
      if (!uv) continue;
      const index = mesh.geometry.index;
      const count = index ? index.count : uv.count;
      const at = (i) => (index ? index.getX(i) : i);
      for (let i = 0; i + 2 < count; i += 3) {
        const [a, b, c] = [at(i), at(i + 1), at(i + 2)];
        path.moveTo(x0 + uv.getX(a) * w, uv.getY(a) * h);
        path.lineTo(x0 + uv.getX(b) * w, uv.getY(b) * h);
        path.lineTo(x0 + uv.getX(c) * w, uv.getY(c) * h);
        path.closePath();
      }
    }
    ctx.lineWidth = Math.max(1, w / 2048);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.stroke(path);
  }

  renderUv({ edges = false } = {}) {
    const sets = Object.keys(this.materials).sort().filter((set) => this.materials[set].map?.image);
    if (!sets.length) throw new Error('no base colour textures to export');
    const images = sets.map((set) => this.materials[set].map.image);
    const width = images.reduce((sum, img) => sum + img.width, 0);
    const height = Math.max(...images.map((img) => img.height));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    let x = 0;
    sets.forEach((set, i) => {
      const img = images[i];
      ctx.drawImage(img, x, 0, img.width, img.height);
      if (edges) this.drawUvEdges(ctx, set, x, img.width, img.height);
      const size = Math.round(img.height / 40);
      ctx.font = `600 ${size}px system-ui, sans-serif`;
      ctx.fillStyle = 'rgba(20, 20, 24, 0.72)';
      ctx.fillRect(x + size * 0.5, size * 0.5, ctx.measureText(set).width + size, size * 1.6);
      ctx.fillStyle = '#eee';
      ctx.fillText(set, x + size, size * 1.7);
      x += img.width;
    });
    return canvas;
  }

  /** Downloads the UV image as `name` (a JPG). */
  async exportUv(name, { edges = false, quality = 0.92 } = {}) {
    const canvas = this.renderUv({ edges });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) throw new Error('could not encode the image');
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: name });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    return { name, width: canvas.width, height: canvas.height, bytes: blob.size };
  }
}
